import React, { useState, useEffect } from 'react';
import { clientsAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  Users, Plus, Search, Building2, Mail, Phone, MapPin,
  FileText, Euro, Edit2, Trash2, ArrowLeft, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ClientsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, client: null });
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    siren: '',
    siret: '',
    vat_number: '',
    default_payment_terms: 'Net 30',
    notes: ''
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async (search = null) => {
    try {
      setLoading(true);
      const response = await clientsAPI.getAll(search);
      setClients(response.data);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les clients',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length > 2 || value.length === 0) {
      loadClients(value || null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postal_code: '',
      country: 'France',
      siren: '',
      siret: '',
      vat_number: '',
      default_payment_terms: 'Net 30',
      notes: ''
    });
    setEditingClient(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingClient) {
        await clientsAPI.update(editingClient.id, formData);
        toast({
          title: 'Client mis à jour',
          description: `${formData.name} a été modifié avec succès`
        });
      } else {
        await clientsAPI.create(formData);
        toast({
          title: 'Client créé',
          description: `${formData.name} a été ajouté à votre carnet`
        });
      }
      setShowAddDialog(false);
      resetForm();
      loadClients();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Une erreur est survenue',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (client) => {
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      city: client.city || '',
      postal_code: client.postal_code || '',
      country: client.country || 'France',
      siren: client.siren || '',
      siret: client.siret || '',
      vat_number: client.vat_number || '',
      default_payment_terms: client.default_payment_terms || 'Net 30',
      notes: client.notes || ''
    });
    setEditingClient(client);
    setShowAddDialog(true);
  };

  const handleDelete = async (client) => {
    setDeleteConfirm({ open: true, client });
  };

  const confirmDelete = async () => {
    const client = deleteConfirm.client;
    if (!client) return;
    
    setDeleting(true);
    try {
      await clientsAPI.delete(client.id);
      toast({
        title: 'Client supprimé',
        description: `${client.name} a été supprimé`
      });
      loadClients();
      setDeleteConfirm({ open: false, client: null });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de supprimer ce client',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard/smartfacture')}
            className="text-slate-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Mes Clients</h1>
            <p className="text-slate-500 text-sm">Gérez votre carnet de clients</p>
          </div>
        </div>

        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingClient ? 'Modifier le client' : 'Nouveau client'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Informations principales */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="name">Nom / Raison sociale *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Entreprise SARL"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="contact@entreprise.fr"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="01 23 45 67 89"
                  />
                </div>
              </div>

              {/* Adresse */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-700">Adresse</h4>
                <div>
                  <Label htmlFor="address">Adresse</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="123 Rue de la République"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="postal_code">Code postal</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                      placeholder="75001"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">Ville</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      placeholder="Paris"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Pays</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                      placeholder="France"
                    />
                  </div>
                </div>
              </div>

              {/* Informations légales */}
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="font-medium text-blue-800">Informations légales (B2B)</h4>
                <p className="text-xs text-blue-600">Obligatoire pour la facturation électronique 2026</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="siren">SIREN</Label>
                    <Input
                      id="siren"
                      value={formData.siren}
                      onChange={(e) => setFormData({...formData, siren: e.target.value})}
                      placeholder="123 456 789"
                      maxLength={11}
                    />
                  </div>
                  <div>
                    <Label htmlFor="siret">SIRET</Label>
                    <Input
                      id="siret"
                      value={formData.siret}
                      onChange={(e) => setFormData({...formData, siret: e.target.value})}
                      placeholder="123 456 789 00012"
                      maxLength={17}
                    />
                  </div>
                  <div>
                    <Label htmlFor="vat_number">N° TVA</Label>
                    <Input
                      id="vat_number"
                      value={formData.vat_number}
                      onChange={(e) => setFormData({...formData, vat_number: e.target.value})}
                      placeholder="FR12345678901"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes">Notes internes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Notes sur ce client..."
                  rows={2}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingClient ? 'Mettre à jour' : 'Créer le client'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Rechercher par nom, SIREN ou email..."
            value={searchTerm}
            onChange={handleSearch}
            className="pl-10"
          />
        </div>
      </div>

      {/* Clients Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : clients.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">Aucun client</h3>
            <p className="text-slate-500 text-sm mb-4">
              Commencez par ajouter votre premier client
            </p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un client
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((client) => (
            <Card key={client.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{client.name}</h3>
                      {client.siren && (
                        <p className="text-xs text-slate-500">SIREN: {client.siren}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(client)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit2 className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(client)}
                      className="h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {client.email && (
                    <div className="flex items-center text-slate-600">
                      <Mail className="h-3.5 w-3.5 mr-2 text-slate-400" />
                      {client.email}
                    </div>
                  )}
                  {client.phone && (
                    <div className="flex items-center text-slate-600">
                      <Phone className="h-3.5 w-3.5 mr-2 text-slate-400" />
                      {client.phone}
                    </div>
                  )}
                  {(client.city || client.address) && (
                    <div className="flex items-center text-slate-600">
                      <MapPin className="h-3.5 w-3.5 mr-2 text-slate-400" />
                      {client.city || client.address}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center text-sm">
                      <FileText className="h-3.5 w-3.5 mr-1 text-slate-400" />
                      <span className="text-slate-600">{client.total_invoices || 0} factures</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Euro className="h-3.5 w-3.5 mr-1 text-slate-400" />
                      <span className="text-slate-600">{(client.total_amount || 0).toFixed(0)}€</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Confirmation de suppression */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open, client: open ? deleteConfirm.client : null })}
        title="Supprimer ce client ?"
        description={`Êtes-vous sûr de vouloir supprimer "${deleteConfirm.client?.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        onConfirm={confirmDelete}
        loading={deleting}
        variant="destructive"
      />
    </div>
  );
};

export default ClientsPage;
